// Park / un-park one initiative (kanban cac323f9, 82f83abb).
//
// ChemMasters admin, 2026-10-05, on /roadmap#initiatives: "I don't see the
// ability to park the 3S housekeeping program or anything." Until then only
// Merlin could set `parked`, from something said in a meeting. An admin parks
// an item here, with a reason if they have one: the owner ruled the reason
// must NOT be required ("I don't want the park reason to be required") and
// asked to be able to speak it, so the box takes a blank and has a Record
// button on the shared dictation hook. A park without a reason still shows as
// the parked_without_reason gap, so Merlin asks the group why next run. The
// server pins the park so later roadmap runs keep it; "Un-park" drops the pin
// and hands back the status the item had when it was parked. Same shape as
// TeamsEditor: an optimistic view of what was just saved until the page's
// re-read catches up.

import { useCallback, useRef, useState } from 'react';
import {
  roadmapReportsApi,
  type InitiativeState,
  type InitiativeStatus,
  type ParkOverride,
} from '../../services/roadmapReports';
import { formatDictationDuration, useVoiceDictation } from '../../hooks/useVoiceDictation';
import { PARK_REASON_MAX, appendSpokenReason, cleanReason } from './parkReason';
import { STATUS_LABELS } from './roadmapViewUtils';
import styles from './RoadmapV2.module.css';

interface Props {
  initiative: InitiativeState;
  /** Admins only; readers see the park note alone. */
  canEdit: boolean;
  /** Called after a successful write so the page re-reads the initiatives. */
  onChanged?: () => void | Promise<void>;
}

function errorMessage(err: unknown): string {
  const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (typeof res?.data?.detail === 'string') return res.data.detail;
  if (res?.status === 403) return 'Only an admin can park an initiative.';
  if (res?.status === 404) return 'This initiative is no longer tracked — reload the page.';
  if (res?.status === 422) return `Keep the reason to ${PARK_REASON_MAX} characters or fewer.`;
  return 'Could not save — try again.';
}

function MicIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

export function ParkNote({ override }: { override: ParkOverride | null | undefined }) {
  if (!override) return null;
  return (
    <div className={styles.teamsPin} data-testid="park-pin">
      Parked by {override.set_by_email} on {override.set_at.slice(0, 10)} · Merlin keeps this parked on later runs
    </div>
  );
}

interface Saved {
  status: InitiativeStatus;
  parked_reason: string | null;
  override: ParkOverride | null;
}

export function ParkControl({ initiative: i, canEdit, onChanged }: Props) {
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);

  // The transcript lands whenever Whisper answers; append to what is in the
  // box THEN, not to what was there when recording started.
  const reasonRef = useRef(reason);
  reasonRef.current = reason;
  const onTranscript = useCallback((transcript: string) => {
    const next = appendSpokenReason(reasonRef.current, transcript);
    reasonRef.current = next;
    setReason(next);
  }, []);
  const dictation = useVoiceDictation({ onTranscript });
  const micBusy = dictation.isRecording || dictation.transcribing || dictation.requestingPermission;

  const status = saved?.status ?? i.status;
  const override = saved ? saved.override : i.park_override ?? null;
  const parked = status === 'parked';

  const start = () => {
    // Re-stating a reason starts from the one on record.
    const initial = override?.parked_reason ?? i.parked_reason ?? '';
    reasonRef.current = initial;
    setReason(initial);
    setError(null);
    setEditing(true);
  };
  const cancel = () => {
    dictation.cancel();
    setEditing(false);
    setError(null);
  };

  const park = async () => {
    if (micBusy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await roadmapReportsApi.parkInitiative(i.initiative_key, cleanReason(reason));
      setSaved({ status: res.status, parked_reason: res.parked_reason, override: res.override });
      setEditing(false);
      await onChanged?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const unpark = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await roadmapReportsApi.unparkInitiative(i.initiative_key);
      setSaved({ status: res.status, parked_reason: res.parked_reason, override: res.override });
      await onChanged?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!canEdit && !override) return null;

  const restoreLabel = override ? STATUS_LABELS[override.previous_status] ?? override.previous_status : 'Unknown';
  const saveLabel = busy ? 'Saving…' : parked ? 'Save reason' : 'Park';
  const saveTitle = dictation.isRecording
    ? 'Stop the recording first'
    : dictation.transcribing
      ? 'Wait for the recording to turn into text'
      : parked
        ? 'Save the reason on record'
        : 'Park it, with or without a reason';

  return (
    <div className={styles.teamsEditor} data-testid="park-control">
      {!editing && canEdit && (
        <div className={styles.teamsRow}>
          {parked ? (
            <>
              <button
                type="button"
                className={styles.respondGhost}
                onClick={() => void unpark()}
                disabled={busy}
                title={`Pick it back up: status returns to ${restoreLabel}; the reason stays on the history`}
              >
                {busy ? 'Working…' : 'Un-park'}
              </button>
              <button type="button" className={styles.respondGhost} onClick={start} disabled={busy}>
                Change the reason
              </button>
            </>
          ) : (
            <button
              type="button"
              className={styles.respondGhost}
              onClick={start}
              disabled={busy}
              title="Sideline it: it leaves the active views but stays tracked, and Merlin keeps it parked"
            >
              Park this initiative
            </button>
          )}
        </div>
      )}

      {editing && (
        <div className={styles.teamsRow} role="group" aria-label={parked ? 'Change the park reason' : 'Park this initiative'}>
          <input
            className={styles.teamsInput}
            style={{ minWidth: 280, flex: 1 }}
            aria-label="Why is it parked?"
            placeholder={
              dictation.isSupported ? 'Why is it parked? (optional — type it or press Record)' : 'Why is it parked? (optional)'
            }
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void park();
              }
            }}
            maxLength={PARK_REASON_MAX}
            disabled={busy}
            autoFocus
          />
          {dictation.isSupported && (
            <button
              type="button"
              className={`${styles.respondMic} ${dictation.isRecording ? styles.respondMicRecording : ''}`}
              onClick={dictation.toggle}
              disabled={busy || dictation.transcribing || dictation.requestingPermission}
              aria-pressed={dictation.isRecording}
              title={dictation.isRecording ? 'Stop and turn it into text' : 'Say the reason instead of typing it'}
            >
              {dictation.isRecording ? <StopIcon /> : <MicIcon />}
              {dictation.isRecording ? 'Stop recording' : 'Record'}
            </button>
          )}
          <button
            type="button"
            className={styles.respondPrimary}
            onClick={() => void park()}
            disabled={busy || micBusy}
            title={saveTitle}
          >
            {saveLabel}
          </button>
          <button type="button" className={styles.respondGhost} onClick={cancel} disabled={busy}>
            Cancel
          </button>
          {dictation.isRecording && (
            <span className={styles.respondRecording} role="status">
              <span className={styles.respondRecordingDot} aria-hidden="true" />
              Recording {formatDictationDuration(dictation.duration)} · talk, then press Stop
            </span>
          )}
          {dictation.requestingPermission && (
            <span className={styles.respondHint} role="status">Waiting for microphone permission…</span>
          )}
          {dictation.transcribing && (
            <span className={styles.respondTranscribing} role="status">
              <span className={styles.respondSpinner} aria-hidden="true" />
              Turning your recording into text…
            </span>
          )}
          {dictation.error && <span className={styles.respondError} role="alert">{dictation.error}</span>}
        </div>
      )}

      <ParkNote override={override} />
      {error && <div className={styles.respondError} role="alert">{error}</div>}
    </div>
  );
}
